// BRIEF 162 §4.3 — THE FLEET VIEW: the operator cockpit's one screen.
//
// Tabs per managed target (from GET /api/fleet/targets). Per target, in the
// order an operator asks the questions: version · tenants (census) · migrations
// · backup · operators · Run checkpoint · Provision tenant. §5.4 adds Health and
// Security. Every panel shows what the target SAID — the answer verbatim plus
// `ms`, red on ok:false — because the cockpit's whole value is that it does not
// interpret. Built from the existing table/panel idiom; colours are theme vars,
// sizes/radii are tokens (the 153 net), every label goes through t().
//
// The seven fleet calls are the only ones typed here (the api() seam is `any`):
// small response interfaces below, cast at the call site. No new primitive.
import { useEffect, useState } from "react";
import {
  ServerCog,
  Building2,
  GitBranch,
  DatabaseBackup,
  UserCog,
  PlayCircle,
  PlusCircle,
  CircleCheck,
  CircleAlert,
  RefreshCw,
  Activity,
  ShieldCheck,
} from "lucide-react";
import { api } from "../api";
import { useTranslation } from "../i18n";
import { RAD, FS } from "../lib/tokens";
import Table from "./Table";

// ── the seven calls, typed ─────────────────────────────────────────────────────
type FleetCall<T> = { ok: boolean; result?: T; error?: unknown; ms: number };
interface VersionInfo {
  buildId?: string; serverId?: string; startedAt?: string;
  bundleStale?: boolean | null; serverStale?: boolean; apiStale?: boolean;
}
interface TargetRow { name: string; url: string; version: FleetCall<VersionInfo> }
interface TargetsResp { isCockpit: boolean; targets: TargetRow[] }
interface TenantRow { id: number; slug: string; name: string; createdAt?: string; census?: { collaborators: number; cases: number; clients: number } }
interface TenantList { tenants: TenantRow[] }
interface MigrationStatus { appliedCount: number; pending: string[]; failed: unknown[]; latest: string | null; healthy: boolean }
interface OperatorRow { id: number; email: string; active: boolean; createdAt?: string }
interface AccountList { operators: OperatorRow[] }

// ── module-scope helpers (stable identity — never define components in render) ──
const mono = "ui-monospace, SFMono-Regular, Menlo, monospace";
const errMsg = (e: unknown): string => (e instanceof Error ? e.message : String(e));

function Ms({ ms }: { ms?: number }) {
  const { t } = useTranslation();
  if (ms == null) return null;
  return <span style={{ color: "var(--tm)", fontSize: FS.s10, fontFamily: mono, marginLeft: 8 }}>{Math.round(ms)} {t("fleet.unit.ms", { fallback: "ms" })}</span>;
}

function Dot({ ok, warn }: { ok: boolean; warn?: boolean }) {
  // BRIEF 162 corr(3) — three states: green ok, amber "cannot verify from this
  // plane" (a design refusal, not a failure), red a real failure.
  if (warn) return <CircleAlert size={14} style={{ color: "var(--am)", flex: "0 0 auto" }} />;
  const Icon = ok ? CircleCheck : CircleAlert;
  return <Icon size={14} style={{ color: ok ? "var(--gn)" : "var(--rd)", flex: "0 0 auto" }} />;
}

function Json({ value }: { value: unknown }) {
  return (
    <pre style={{
      margin: 0, padding: 10, background: "var(--bi)", border: "1px solid var(--br)",
      borderRadius: RAD.r6, color: "var(--ts)", fontSize: FS.s11, fontFamily: mono,
      overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word",
    }}>{typeof value === "string" ? value : JSON.stringify(value, null, 2)}</pre>
  );
}

// A titled panel around one call's result. `bad` forces the red header when a
// value inside the ok:true result is itself an alarm (a stale flag, an old dump).
function Panel({ title, icon, call, loading, bad, amber, children }: {
  title: string; icon: React.ReactNode; call?: FleetCall<unknown> | null;
  loading?: boolean; bad?: boolean; amber?: boolean; children?: React.ReactNode;
}) {
  const ok = !!call && call.ok && !bad;
  return (
    <div style={{ border: "1px solid var(--br)", borderRadius: RAD.r8, marginBottom: 12, overflow: "hidden" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: 8, padding: "8px 12px",
        borderBottom: "1px solid var(--br)", background: "var(--bc)",
      }}>
        <span style={{ display: "inline-flex", color: "var(--tm)" }}>{icon}</span>
        <span style={{ fontSize: FS.s12, fontWeight: 600, color: "var(--tp)" }}>{title}</span>
        {call && <span style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center" }}><Dot ok={ok} warn={amber} /><Ms ms={call.ms} /></span>}
      </div>
      <div style={{ padding: 12 }}>
        {loading ? <span style={{ color: "var(--tm)", fontSize: FS.s11 }}>…</span> : children}
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: "left", padding: "4px 8px", color: "var(--tm)", fontSize: FS.s10, fontWeight: 600, borderBottom: "1px solid var(--br)" };
const td: React.CSSProperties = { padding: "4px 8px", color: "var(--ts)", fontSize: FS.s11, borderBottom: "1px solid var(--br)" };

// ── the view ───────────────────────────────────────────────────────────────────
export default function FleetView() {
  const { t } = useTranslation();
  const [boot, setBoot] = useState<{ loading: boolean; resp?: TargetsResp; err?: string }>({ loading: true });
  const [tab, setTab] = useState<string | null>(null);

  // per-target read state, keyed by target name
  const [reads, setReads] = useState<Record<string, {
    tenants?: FleetCall<TenantList>; migrations?: FleetCall<MigrationStatus>;
    backup?: FleetCall<unknown>; operators?: FleetCall<AccountList>; loading?: boolean; err?: string;
  }>>({});
  const [cp, setCp] = useState<Record<string, { running?: boolean; resp?: FleetCall<unknown>; err?: string }>>({});
  const [prov, setProv] = useState<Record<string, { slug: string; name: string; running?: boolean; resp?: unknown; err?: string }>>({});
  // §5.4 — the two read-only host operations, button-triggered like the checkpoint.
  const [hh, setHh] = useState<Record<string, { running?: boolean; resp?: FleetCall<unknown>; err?: string }>>({});
  const [hs, setHs] = useState<Record<string, { running?: boolean; resp?: FleetCall<unknown>; err?: string }>>({});

  useEffect(() => {
    let live = true;
    (api.get("/fleet/targets") as Promise<TargetsResp>)
      .then((r: TargetsResp) => { if (!live) return; setBoot({ loading: false, resp: r }); if (r.targets?.[0]) setTab(r.targets[0].name); })
      .catch((e) => { if (live) setBoot({ loading: false, err: errMsg(e) }); });
    return () => { live = false; };
  }, []);

  async function loadReads(name: string) {
    setReads((s) => ({ ...s, [name]: { ...s[name], loading: true, err: undefined } }));
    try {
      const [tenants, migrations, backup, operators] = await Promise.all([
        api.get(`/fleet/${name}/tenants`) as Promise<FleetCall<TenantList>>,
        api.get(`/fleet/${name}/migrations`) as Promise<FleetCall<MigrationStatus>>,
        api.get(`/fleet/${name}/backup`) as Promise<FleetCall<unknown>>,
        api.get(`/fleet/${name}/operators`) as Promise<FleetCall<AccountList>>,
      ]);
      setReads((s) => ({ ...s, [name]: { tenants, migrations, backup, operators, loading: false } }));
    } catch (e) {
      setReads((s) => ({ ...s, [name]: { ...s[name], loading: false, err: errMsg(e) } }));
    }
  }

  // Load the reads when the active target changes. Deps are [tab] only: the
  // effect writes `reads`, so listing `reads` here would be the effect-sets-its-
  // own-dep shape — and re-fetching on a tab switch is the wanted behaviour.
  useEffect(() => { if (tab) loadReads(tab); }, [tab]);

  async function runCheckpoint(name: string) {
    setCp((s) => ({ ...s, [name]: { running: true } }));
    try {
      const resp = await api.post(`/fleet/${name}/checkpoint`, {}) as FleetCall<unknown>;
      setCp((s) => ({ ...s, [name]: { running: false, resp } }));
    } catch (e) {
      setCp((s) => ({ ...s, [name]: { running: false, err: errMsg(e) } }));
    }
  }

  // §5.4 — one runner for both host operations (health, security).
  async function runHost(name: string, kind: "health" | "security", setter: typeof setHh) {
    setter((s) => ({ ...s, [name]: { running: true } }));
    try {
      const resp = await api.post(`/fleet/${name}/${kind}`, {}) as FleetCall<unknown>;
      setter((s) => ({ ...s, [name]: { running: false, resp } }));
    } catch (e) {
      setter((s) => ({ ...s, [name]: { running: false, err: errMsg(e) } }));
    }
  }

  async function provision(name: string) {
    const f = prov[name] || { slug: "", name: "" };
    setProv((s) => ({ ...s, [name]: { ...f, running: true, err: undefined, resp: undefined } }));
    try {
      const resp = await api.post(`/fleet/${name}/tenants`, { slug: f.slug, name: f.name });
      setProv((s) => ({ ...s, [name]: { ...f, running: false, resp } }));
    } catch (e) {
      setProv((s) => ({ ...s, [name]: { ...f, running: false, err: errMsg(e) } }));
    }
  }

  if (boot.loading) return <div className="vw" style={{ padding: 16 }}><p style={{ color: "var(--tm)" }}>{t("common.loading", { fallback: "Loading…" })}</p></div>;

  if (boot.err) return (
    <div className="vw" style={{ padding: 16 }}>
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><ServerCog size={20} /> {t("fleet.title", { fallback: "Fleet" })}</h2>
      <p style={{ color: "var(--rd)", fontSize: FS.s12 }}>{boot.err}</p>
    </div>
  );

  const targets = boot.resp?.targets || [];
  if (!boot.resp?.isCockpit || !targets.length) return (
    <div className="vw" style={{ padding: 16 }}>
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}><ServerCog size={20} /> {t("fleet.title", { fallback: "Fleet" })}</h2>
      <p style={{ color: "var(--tm)", fontSize: FS.s12, maxWidth: 640 }}>{t("fleet.not_cockpit", { fallback: "This instance holds no managed targets — it is not a cockpit." })}</p>
    </div>
  );

  const active = targets.find((x) => x.name === tab) || targets[0];
  const r = reads[active.name] || {};
  const mig = r.migrations?.ok ? r.migrations.result : undefined;
  // corr(3) — the backup probe answered "cannot verify from this plane" (a
  // design refusal, root-only key) → amber, not red.
  const backupUnver = !!r.backup && !r.backup.ok && (r.backup.error as { unverifiable?: boolean } | undefined)?.unverifiable === true;

  return (
    <div className="vw" style={{ padding: 16 }}>
      <h2 style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ServerCog size={20} /> {t("fleet.title", { fallback: "Fleet" })}
        <span style={{ marginLeft: "auto" }}>
          <button className="bt" onClick={() => loadReads(active.name)} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s11 }}>
            <RefreshCw size={13} /> {t("fleet.refresh", { fallback: "Refresh" })}
          </button>
        </span>
      </h2>

      {/* target tabs */}
      <div style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--br)", marginBottom: 14 }}>
        {targets.map((tg) => {
          const on = tg.name === active.name;
          return (
            /* 188 (7) — A TOP-TAB, left deliberately: `RAD.tabTop` corners and
               a bottom border that merges into the strip's rule when on.
               SegmentedControl is a joined pill and this is not one; S3c's
               button pass takes these as buttons. No later pass reads "tab"
               here and converts it. Measured in docs/188-switcher-strips.md. */
            <button key={tg.name} onClick={() => setTab(tg.name)} style={{
              padding: "6px 12px", cursor: "pointer", fontSize: FS.s12,
              background: on ? "var(--as)" : "transparent",
              color: on ? "var(--ac)" : "var(--tm)",
              border: "1px solid " + (on ? "var(--ac)" : "var(--br)"),
              borderBottom: on ? "1px solid var(--as)" : "1px solid var(--br)",
              borderRadius: RAD.tabTop,
            }}>{tg.name}</button>
          );
        })}
      </div>

      {/* version */}
      <Panel title={t("fleet.version", { fallback: "Version" })} icon={<ServerCog size={15} />} call={active.version}
        bad={!!active.version.ok && !!active.version.result && (active.version.result.bundleStale === true || active.version.result.serverStale === true || active.version.result.apiStale === true)}>
        {active.version.ok && active.version.result ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 20px", fontSize: FS.s11 }}>
            <span style={{ color: "var(--tm)" }}>{t("fleet.f.buildId", { fallback: "buildId" })} <b style={{ color: "var(--tp)", fontFamily: mono }}>{active.version.result.buildId}</b></span>
            <span style={{ color: "var(--tm)" }}>{t("fleet.f.serverId", { fallback: "serverId" })} <b style={{ color: "var(--tp)", fontFamily: mono }}>{active.version.result.serverId}</b></span>
            <span style={{ color: active.version.result.bundleStale ? "var(--rd)" : "var(--tm)" }}>{t("fleet.f.bundleStale", { fallback: "bundleStale" })} {String(active.version.result.bundleStale)}</span>
            <span style={{ color: active.version.result.serverStale ? "var(--rd)" : "var(--tm)" }}>{t("fleet.f.serverStale", { fallback: "serverStale" })} {String(active.version.result.serverStale)}</span>
            <span style={{ color: active.version.result.apiStale ? "var(--rd)" : "var(--tm)" }}>{t("fleet.f.apiStale", { fallback: "apiStale" })} {String(active.version.result.apiStale)}</span>
            <span style={{ color: "var(--tm)" }}>{t("fleet.f.startedAt", { fallback: "startedAt" })} <span style={{ color: "var(--ts)" }}>{active.version.result.startedAt}</span></span>
          </div>
        ) : <Json value={active.version.error} />}
      </Panel>

      {/* tenants */}
      <Panel title={t("fleet.tenants", { fallback: "Tenants" })} icon={<Building2 size={15} />} call={r.tenants} loading={r.loading}>
        {r.tenants?.ok && r.tenants.result ? (
          <div style={{ overflowX: "auto" }}>
            <Table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr>
                <th style={th}>{t("fleet.col.id", { fallback: "id" })}</th><th style={th}>{t("fleet.col.slug", { fallback: "slug" })}</th><th style={th}>{t("fleet.col.name", { fallback: "name" })}</th>
                <th style={th}>{t("fleet.col.collaborators", { fallback: "coll." })}</th><th style={th}>{t("fleet.col.cases", { fallback: "cases" })}</th><th style={th}>{t("fleet.col.clients", { fallback: "clients" })}</th>
              </tr></thead>
              <tbody>{r.tenants.result.tenants.map((tn) => (
                <tr key={tn.id}>
                  <td style={td}>{tn.id}</td><td style={td}>{tn.slug}</td><td style={td}>{tn.name}</td>
                  <td style={td}>{tn.census?.collaborators ?? "—"}</td><td style={td}>{tn.census?.cases ?? "—"}</td><td style={td}>{tn.census?.clients ?? "—"}</td>
                </tr>
              ))}</tbody>
            </Table>
          </div>
        ) : r.tenants ? <Json value={r.tenants.error} /> : null}
      </Panel>

      {/* migrations */}
      <Panel title={t("fleet.migrations", { fallback: "Migrations" })} icon={<GitBranch size={15} />} call={r.migrations} loading={r.loading} bad={!!mig && !mig.healthy}>
        {mig ? (
          <div style={{ fontSize: FS.s11, color: "var(--ts)" }}>
            <div>{t("fleet.mig.applied", { fallback: "applied" })}: <b style={{ color: "var(--tp)" }}>{mig.appliedCount}</b> · {t("fleet.mig.latest", { fallback: "latest" })} <span style={{ fontFamily: mono }}>{mig.latest}</span></div>
            {mig.pending?.length ? <div style={{ color: "var(--am)" }}>{t("fleet.mig.pending", { fallback: "pending" })}: {mig.pending.join(", ")}</div> : <div style={{ color: "var(--gn)" }}>{t("fleet.mig.none_pending", { fallback: "none pending" })}</div>}
            {mig.failed?.length ? <div style={{ color: "var(--rd)" }}>{t("fleet.mig.failed", { fallback: "failed" })}: {mig.failed.length}</div> : null}
          </div>
        ) : r.migrations ? <Json value={r.migrations.error} /> : null}
      </Panel>

      {/* backup — corr(3): amber "cannot verify from this plane", never red, when the probe refuses by design */}
      <Panel title={t("fleet.backup", { fallback: "Backup" })} icon={<DatabaseBackup size={15} />} call={r.backup} loading={r.loading} amber={backupUnver}>
        {backupUnver
          ? <p style={{ color: "var(--am)", fontSize: FS.s11, margin: 0 }}>{t("fleet.backup.unverifiable", { fallback: "not verifiable from this plane" })}</p>
          : r.backup ? <Json value={r.backup.ok ? r.backup.result : r.backup.error} /> : null}
      </Panel>

      {/* operators */}
      <Panel title={t("fleet.operators", { fallback: "Operators" })} icon={<UserCog size={15} />} call={r.operators} loading={r.loading}>
        {r.operators?.ok && r.operators.result ? (
          <div style={{ overflowX: "auto" }}>
            <Table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead><tr><th style={th}>{t("fleet.col.id", { fallback: "id" })}</th><th style={th}>{t("fleet.col.email", { fallback: "email" })}</th><th style={th}>{t("fleet.col.active", { fallback: "active" })}</th><th style={th}>{t("fleet.col.created", { fallback: "created" })}</th></tr></thead>
              <tbody>{r.operators.result.operators.map((o) => (
                <tr key={o.id}><td style={td}>{o.id}</td><td style={td}>{o.email}</td>
                  <td style={{ ...td, color: o.active ? "var(--gn)" : "var(--tm)" }}>{String(o.active)}</td><td style={td}>{o.createdAt}</td></tr>
              ))}</tbody>
            </Table>
          </div>
        ) : r.operators ? <Json value={r.operators.error} /> : null}
      </Panel>

      {/* checkpoint */}
      <Panel title={t("fleet.checkpoint", { fallback: "Checkpoint" })} icon={<PlayCircle size={15} />} call={cp[active.name]?.resp}>
        <button className="bt" disabled={cp[active.name]?.running} onClick={() => runCheckpoint(active.name)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s11, marginBottom: cp[active.name] ? 10 : 0 }}>
          <PlayCircle size={13} /> {cp[active.name]?.running ? t("fleet.checkpoint.running", { fallback: "Running…" }) : t("fleet.checkpoint.run", { fallback: "Run checkpoint" })}
        </button>
        {cp[active.name]?.err && <p style={{ color: "var(--rd)", fontSize: FS.s11 }}>{cp[active.name]?.err}</p>}
        {cp[active.name]?.resp && <Json value={cp[active.name]?.resp?.ok ? cp[active.name]?.resp?.result : cp[active.name]?.resp?.error} />}
      </Panel>

      {/* §5.4 — health */}
      <Panel title={t("fleet.health", { fallback: "Health" })} icon={<Activity size={15} />} call={hh[active.name]?.resp}>
        <button className="bt" disabled={hh[active.name]?.running} onClick={() => runHost(active.name, "health", setHh)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s11, marginBottom: hh[active.name] ? 10 : 0 }}>
          <Activity size={13} /> {hh[active.name]?.running ? t("fleet.running", { fallback: "Running…" }) : t("fleet.health.run", { fallback: "Check health" })}
        </button>
        {hh[active.name]?.err && <p style={{ color: "var(--rd)", fontSize: FS.s11 }}>{hh[active.name]?.err}</p>}
        {hh[active.name]?.resp && <Json value={hh[active.name]?.resp?.ok ? hh[active.name]?.resp?.result : hh[active.name]?.resp?.error} />}
      </Panel>

      {/* §5.4 — security */}
      <Panel title={t("fleet.security", { fallback: "Security" })} icon={<ShieldCheck size={15} />} call={hs[active.name]?.resp}>
        <button className="bt" disabled={hs[active.name]?.running} onClick={() => runHost(active.name, "security", setHs)}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s11, marginBottom: hs[active.name] ? 10 : 0 }}>
          <ShieldCheck size={13} /> {hs[active.name]?.running ? t("fleet.running", { fallback: "Running…" }) : t("fleet.security.run", { fallback: "Check security" })}
        </button>
        {hs[active.name]?.err && <p style={{ color: "var(--rd)", fontSize: FS.s11 }}>{hs[active.name]?.err}</p>}
        {hs[active.name]?.resp && <Json value={hs[active.name]?.resp?.ok ? hs[active.name]?.resp?.result : hs[active.name]?.resp?.error} />}
      </Panel>

      {/* provision */}
      <Panel title={t("fleet.provision", { fallback: "Provision tenant" })} icon={<PlusCircle size={15} />}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <input className="in" placeholder="slug" value={prov[active.name]?.slug || ""}
            onChange={(e) => setProv((s) => ({ ...s, [active.name]: { ...(s[active.name] || { slug: "", name: "" }), slug: e.target.value } }))}
            style={{ fontSize: FS.s12, maxWidth: 180 }} />
          <input autoComplete="off" /* 207 §18 */ className="in no-contact-autofill" placeholder={t("fleet.col.name", { fallback: "name" })} value={prov[active.name]?.name || ""}
            onChange={(e) => setProv((s) => ({ ...s, [active.name]: { ...(s[active.name] || { slug: "", name: "" }), name: e.target.value } }))}
            style={{ fontSize: FS.s12, maxWidth: 220 }} />
          <button className="bt" disabled={prov[active.name]?.running || !prov[active.name]?.slug || !prov[active.name]?.name} onClick={() => provision(active.name)}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: FS.s11 }}>
            <PlusCircle size={13} /> {prov[active.name]?.running ? t("fleet.provision.running", { fallback: "Provisioning…" }) : t("fleet.provision.go", { fallback: "Provision" })}
          </button>
        </div>
        {prov[active.name]?.err && <p style={{ color: "var(--rd)", fontSize: FS.s11, marginTop: 8 }}>{prov[active.name]?.err}</p>}
        {prov[active.name]?.resp != null && <div style={{ marginTop: 8 }}><Json value={prov[active.name]?.resp} /></div>}
      </Panel>
    </div>
  );
}
